import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Plus, Trash2, X, ChevronLeft, Pencil, Filter, ExternalLink,
  Building2, Eye, EyeOff, CalendarClock,
} from "lucide-react";

/* DeskDetail — generic CRUD page driven by /agency/desk-schemas.
 *
 * Route: /office/desks/:kind
 * Kinds it handles directly: client, brief, invoice, bid, ppm, campaign,
 * content, creator. (Site inventory has its own dedicated page.)
 * The `client` kind row is clickable → /office/desks/client/:id
 */
const _LINKABLE_TO_CLIENT = new Set(["brief", "bid", "invoice", "campaign", "ppm", "content"]);
export default function DeskDetail() {
  const { kind } = useParams();
  const nav = useNavigate();

  // Handoff to the dedicated Site Inventory page for OOH
  useEffect(() => {
    if (kind === "site") nav("/office/desks/site", { replace: true });
  }, [kind, nav]);

  const [schemas, setSchemas] = useState(null);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const schema = schemas?.[kind];
  const publicEligible = !!schema?.public;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cat }, { data: list }] = await Promise.all([
        api.get("/agency/desk-schemas"),
        api.get(`/agency/desks/${kind}${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`),
      ]);
      setSchemas(cat.schemas);
      setItems(list.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load");
    } finally { setLoading(false); }
  }, [kind, statusFilter]);
  useEffect(() => { load(); }, [load]);

  if (!schema && !loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center">
        <div className="cs-card rounded-2xl p-10">
          <h1 className="font-display text-2xl uppercase tracking-tight">Unknown desk</h1>
          <p className="mt-2 text-sm text-white/60">The desk "{kind}" isn&apos;t in the catalogue.</p>
          <Link to="/dashboard" className="mt-4 inline-block text-[10px] tracking-[0.24em] uppercase underline">Back</Link>
        </div>
      </div>
    );
  }
  if (!schema) return null;

  const editingItem = editingId ? items.find((i) => i.id === editingId) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8"
         data-testid={`desk-page-${kind}`}>
      <Link to="/dashboard"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-white/50 hover:text-white transition"
            data-testid="desk-back">
        <ChevronLeft size={12} /> Back to Office
      </Link>
      <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            Agency Office · Desk
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-1">
            {schema.label}s
          </h1>
          <p className="mt-1.5 text-[12.5px] text-white/50">
            {items.length} item{items.length === 1 ? "" : "s"}
            {publicEligible && (
              <span className="ml-2 text-[10px] tracking-[0.22em] uppercase text-emerald-300">
                · public-eligible
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusFilter statuses={schema.statuses} value={statusFilter} on={setStatusFilter} />
          <button
            onClick={() => { setEditingId(null); setShowForm(true); }}
            className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.18em] uppercase font-semibold bg-white text-black hover:bg-white/90 inline-flex items-center gap-1.5"
            data-testid={`desk-new-${kind}-btn`}
          >
            <Plus size={12} /> New {schema.label}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 text-sm text-white/45">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-8 cs-card rounded-2xl p-12 text-center" data-testid={`desk-empty-${kind}`}>
          <div className="w-14 h-14 rounded-full bg-white/8 mx-auto flex items-center justify-center text-white/50">
            <Building2 size={22} />
          </div>
          <div className="mt-4 font-display text-lg uppercase tracking-tight">Nothing here yet</div>
          <p className="mt-1.5 text-sm text-white/50 max-w-md mx-auto">
            Add your first {schema.label.toLowerCase()} to get started.
            {publicEligible && " Public items will show up on your public agency profile."}
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-2" data-testid={`desk-list-${kind}`}>
          {items.map((it) => (
            <ItemRow key={it.id} item={it} schema={schema}
                     onEdit={() => { setEditingId(it.id); setShowForm(true); }}
                     onDeleted={load} onTogglePublic={load} kind={kind} />
          ))}
        </div>
      )}

      {showForm && (
        <ItemFormModal
          kind={kind} schema={schema} item={editingItem} publicEligible={publicEligible}
          onClose={() => { setShowForm(false); setEditingId(null); }}
          onSaved={() => { setShowForm(false); setEditingId(null); load(); }}
        />
      )}
    </div>
  );
}

/* ---- filter + row ------------------------------------------------------- */

function StatusFilter({ statuses, value, on }) {
  return (
    <div className="flex items-center gap-1.5">
      <Filter size={13} className="text-white/40" />
      <select value={value} onChange={(e) => on(e.target.value)}
              className="bg-black/40 border border-white/15 rounded-md h-9 px-2 text-[11.5px]"
              data-testid="desk-status-filter">
        <option value="all">All statuses</option>
        {statuses.map((s) => <option key={s} value={s}>{prettify(s)}</option>)}
      </select>
    </div>
  );
}

function ItemRow({ item, schema, onEdit, onDeleted, onTogglePublic, kind }) {
  const del = async () => {
    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/agency/desks/${kind}/${item.id}`);
      toast.success("Deleted");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const togglePublic = async () => {
    try {
      await api.patch(`/agency/desks/${kind}/${item.id}`, { is_public: !item.is_public });
      toast.success(item.is_public ? "Hidden from public profile" : "Now shown on public profile");
      onTogglePublic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  // Show 2-3 field values inline
  const preview = schema.fields.slice(1, 5)
    .map((f) => ({ label: f.label, val: item.data?.[f.key] }))
    .filter((x) => x.val !== undefined && x.val !== "" && x.val !== null)
    .slice(0, 3);

  const isClient = kind === "client";
  const rowInner = (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-base uppercase tracking-tight truncate">{item.title}</div>
          <StatusChip s={item.status} />
          {schema.public && (
            <span className={"px-2 py-0.5 rounded-full text-[9px] tracking-[0.22em] uppercase font-semibold " +
              (item.is_public ? "bg-emerald-500/15 text-emerald-300" : "bg-white/8 text-white/50")}
              data-testid={`desk-public-flag-${item.id}`}>
              {item.is_public ? "Public" : "Private"}
            </span>
          )}
        </div>
        {preview.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-white/55">
            {preview.map((p, i) => (
              <span key={i}><span className="text-white/35">{p.label}:</span> {String(p.val)}</span>
            ))}
          </div>
        )}
        {isClient && (
          <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-white/40">
            Click to open · timeline + revenue
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="cs-card rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap"
         data-testid={`desk-row-${item.id}`}>
      {isClient ? (
        <Link to={`/office/desks/client/${item.id}`} className="min-w-0 flex-1 hover:opacity-90"
              data-testid={`desk-client-open-${item.id}`}>
          {rowInner}
        </Link>
      ) : rowInner}
      <div className="flex items-center gap-1">
        {schema.public && (
          <button onClick={togglePublic}
            className="p-2 rounded-md hover:bg-white/8 text-white/50 hover:text-white"
            title={item.is_public ? "Make private" : "Make public"}
            data-testid={`desk-toggle-public-${item.id}`}>
            {item.is_public ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
        )}
        <button onClick={onEdit}
          className="p-2 rounded-md hover:bg-white/8 text-white/60 hover:text-white"
          data-testid={`desk-edit-${item.id}`}>
          <Pencil size={13} />
        </button>
        <button onClick={del}
          className="p-2 rounded-md hover:bg-red-500/15 text-white/50 hover:text-red-300"
          data-testid={`desk-delete-${item.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function StatusChip({ s }) {
  return (
    <span className="px-2 py-0.5 rounded-full text-[9.5px] tracking-[0.22em] uppercase font-semibold bg-white/8 text-white/70">
      {prettify(s)}
    </span>
  );
}

/* ---- modal form --------------------------------------------------------- */

function ItemFormModal({ kind, schema, item, publicEligible, onClose, onSaved }) {
  const initial = useMemo(() => {
    const base = {
      title: item?.title || "",
      status: item?.status || schema.statuses[0],
      data: { ...(item?.data || {}) },
      is_public: item ? !!item.is_public : true,
      client_id: item?.client_id || "",
    };
    return base;
  }, [item, schema.statuses]);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);

  const linkable = _LINKABLE_TO_CLIENT.has(kind);
  useEffect(() => {
    if (!linkable) return;
    api.get("/agency/desks/client").then(({ data }) => setClients(data.items || []));
  }, [linkable]);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const updData = (k, v) => setForm((s) => ({ ...s, data: { ...s.data, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    // Check required
    for (const f of schema.fields) {
      if (f.required && !form.data[f.key]) {
        toast.error(`Missing: ${f.label}`);
        return;
      }
    }
    setBusy(true);
    try {
      const body = { title: form.title, status: form.status, data: form.data };
      if (publicEligible) body.is_public = form.is_public;
      if (linkable)       body.client_id = form.client_id || "";
      if (item) {
        await api.patch(`/agency/desks/${kind}/${item.id}`, body);
      } else {
        await api.post(`/agency/desks/${kind}`, body);
      }
      toast.success(item ? "Updated" : "Created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
         data-testid={`desk-form-modal-${kind}`}>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit}
            className="relative z-10 w-full max-w-2xl cs-card rounded-2xl p-6 my-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              {item ? "Edit" : "New"} · {schema.label}
            </div>
            <div className="mt-0.5 font-display text-xl uppercase tracking-tight">
              {schema.label} details
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-white/8 text-white/60"
                  data-testid="desk-form-close">
            <X size={16} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {schema.fields.map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                {f.label} {f.required && <span className="text-red-400">*</span>}
              </label>
              <FieldInput f={f} value={form.data[f.key] ?? ""}
                          onChange={(v) => updData(f.key, v)}
                          testid={`desk-field-${f.key}`} />
            </div>
          ))}
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Status</label>
            <select value={form.status} onChange={(e) => upd("status", e.target.value)}
                    className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
                    data-testid="desk-form-status">
              {schema.statuses.map((s) => <option key={s} value={s}>{prettify(s)}</option>)}
            </select>
          </div>
          {publicEligible && (
            <label className="flex items-end pb-1 gap-2 text-[12px] text-white/70"
                   data-testid="desk-form-public-wrap">
              <input type="checkbox" checked={form.is_public}
                     onChange={(e) => upd("is_public", e.target.checked)}
                     data-testid="desk-form-public" />
              Show on public agency profile
            </label>
          )}
          {linkable && (
            <div className={publicEligible ? "sm:col-span-2" : ""}>
              <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Link to Client (optional)
              </label>
              <select value={form.client_id} onChange={(e) => upd("client_id", e.target.value)}
                className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
                data-testid="desk-form-client">
                <option value="">— unlinked —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
              <div className="mt-1 text-[10.5px] text-white/45">
                Linking rolls this item up into the client&apos;s revenue view.
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="h-10 px-4 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold border border-white/25 hover:bg-white/8">
            Cancel
          </button>
          <button type="submit" disabled={busy}
                  className="h-10 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60"
                  data-testid="desk-form-submit">
            {busy ? "Saving…" : (item ? "Update" : "Create")}
          </button>
        </div>
      </form>
    </div>
  );
}

function FieldInput({ f, value, onChange, testid }) {
  if (f.type === "textarea")
    return <textarea value={value} onChange={(e) => onChange(e.target.value)}
             className="mt-1 w-full h-24 bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm resize-none"
             data-testid={testid} />;
  if (f.type === "select")
    return <select value={value} onChange={(e) => onChange(e.target.value)}
             className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
             data-testid={testid}>
             <option value="">—</option>
             {(f.options || []).map((o) => <option key={o} value={o}>{prettify(o)}</option>)}
           </select>;
  const t = f.type === "number" ? "number" :
            f.type === "email"  ? "email"  :
            f.type === "url"    ? "url"    :
            f.type === "date"   ? "date"   : "text";
  return <input type={t} value={value}
           onChange={(e) => onChange(e.target.value)}
           className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
           data-testid={testid} />;
}

function prettify(s) {
  return String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export { prettify };
